import React, { ReactNode } from 'react';
import cn from 'classnames';
import TextButton from '../TextButton';
import TextButtonLink from '../TextButtonLink';
import Tooltip from '../Tooltip';

import styles from './DealsBanner.module.scss';

export type DealsBannerVariant =
  | 'promos'
  | 'membershipDiscount'
  | 'dealsClearance';

export interface DealsBannerProps {
  variant: DealsBannerVariant;
  title?: string;
  firstLine?: string | ReactNode;
  descriptionLine?: string | ReactNode;
  infoTooltipContent?: ReactNode;
  ctaLabel?: string;
  ctaHref?: string;
  className?: string;
  ariaLabel?: string;
  tooltipPlacement?: 'title' | 'description' | 'firstLine';
}

const DealsBanner = ({
  variant,
  title,
  firstLine,
  descriptionLine,
  infoTooltipContent,
  ctaLabel,
  ctaHref,
  className,
  ariaLabel,
  tooltipPlacement,
}: DealsBannerProps) => {
  const showCta = variant === 'membershipDiscount' && ctaLabel;

  const tooltipSuffix: ReactNode = infoTooltipContent ? (
    <>
      {' '}
      <span className={styles.tooltipInline}>
        <Tooltip
          content={infoTooltipContent}
          className={styles.tooltipTrigger}
        />
      </span>
    </>
  ) : null;

  // Injects tooltipSuffix into the last <p> of a React element (e.g. DealsBannerMarkdown),
  // or appends it inline for plain string/node content.
  const withSuffix = (content: ReactNode): ReactNode => {
    if (!tooltipSuffix) return content;
    if (React.isValidElement(content)) {
      return React.cloneElement(
        content as React.ReactElement<{ suffix?: ReactNode }>,
        { suffix: tooltipSuffix }
      );
    }
    return (
      <>
        {content}
        {tooltipSuffix}
      </>
    );
  };

  return (
    <section
      className={cn(styles.root, styles[variant], className)}
      role="status"
      aria-label={ariaLabel ?? title}
    >
      {title && (
        <div className={styles.titleRow}>
          <div className={styles.title}>{title}</div>
          {tooltipPlacement === 'title' && infoTooltipContent && (
            <span className={styles.tooltipInline}>
              <Tooltip
                content={infoTooltipContent}
                className={cn(styles.tooltipTrigger, styles.titleTooltip)}
              />
            </span>
          )}
        </div>
      )}
      {firstLine && (
        <div className={styles.bodyText}>
          {tooltipPlacement === 'firstLine' ? withSuffix(firstLine) : firstLine}
        </div>
      )}
      {descriptionLine && (
        <div className={styles.descriptionRow}>
          <div className={styles.bodyText}>
            {tooltipPlacement === 'description'
              ? withSuffix(descriptionLine)
              : descriptionLine}
          </div>
        </div>
      )}
      {showCta && (
        <div className={styles.ctaWrap}>
          {ctaHref?.trim() ? (
            <TextButtonLink href={ctaHref.trim()} size="medium">
              {ctaLabel}
            </TextButtonLink>
          ) : (
            <TextButton
              type="tertiary"
              size="medium"
              label={ctaLabel}
              padding="default"
            />
          )}
        </div>
      )}
    </section>
  );
};

DealsBanner.displayName = 'DealsBanner';

export default DealsBanner;
